/**
 * Settings → Localização (#229): ver/alterar a localização do restaurante
 * com RECONFIRMAÇÃO obrigatória, confirmação em 1 toque da sugestão do
 * backfill (#230) e link "Abrir no Google Maps" (#231). Sem mapa embutido
 * (decisão da issue: link em vez de dependência pesada) — a busca de
 * endereço usa os endpoints públicos /places/* (#227, chave no servidor).
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { locationApi, newPlacesSession, type ResolvedPlace } from '../lib/api.js'
import { Button, Input, Select, Spinner, toast, Badge } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

type Mode = 'view' | 'search' | 'manual'

export function Location() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [mode, setMode] = useState<Mode>('view')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<ResolvedPlace | null>(null)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [manual, setManual] = useState({ street: '', postal: '', city: '', country: 'PT' })
  const [suggestBusy, setSuggestBusy] = useState(false)
  const [suggestions, setSuggestions] = useState<
    { place_id: string; label: string; secondary: string | null }[]
  >([])
  const [pin, setPin] = useState<{ lat: string; lng: string }>({ lat: '', lng: '' })
  // session token do Places (#394): 1 por sessão de busca — autocomplete
  // emite, resolve consome; aqui renovamos a cada resolve concluído
  const [session, setSession] = useState(newPlacesSession)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-location'],
    queryFn: () => locationApi.get(),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin-location'] })
    void qc.invalidateQueries({ queryKey: ['location-banner'] })
  }

  const confirmMut = useMutation({
    mutationFn: () => locationApi.confirmSuggestion(),
    onSuccess: () => {
      toast.success(t('admin.location.confirmed'))
      invalidate()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const updateMut = useMutation({
    mutationFn: (address: Parameters<typeof locationApi.update>[0]) => locationApi.update(address),
    onSuccess: () => {
      toast.success(t('admin.location.saved'))
      setMode('view')
      setPicked(null)
      setConfirmChecked(false)
      setQuery('')
      setSuggestions([])
      setPin({ lat: '', lng: '' })
      invalidate()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  async function search(v: string) {
    if (v.trim().length < 2) {
      setSuggestions([])
      return
    }
    setSuggestBusy(true)
    try {
      const { suggestions: s } = await locationApi.autocomplete(v.trim(), session)
      setSuggestions(s)
    } catch {
      setSuggestions([])
      toast.error(t('admin.location.search_unavailable'))
    } finally {
      setSuggestBusy(false)
    }
  }

  async function choose(placeId: string) {
    try {
      const { place } = await locationApi.resolve(placeId, session)
      setPicked(place)
      setQuery(place.formatted_address)
      setSuggestions([])
      setSession(newPlacesSession()) // sessão consumida no resolve (#394)
      setPin({
        lat: place.lat !== null ? String(place.lat) : '',
        lng: place.lng !== null ? String(place.lng) : '',
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('admin.location.search_unavailable'))
    }
  }

  function savePicked() {
    if (!picked) return
    const lat = Number.parseFloat(pin.lat)
    const lng = Number.parseFloat(pin.lng)
    const movedPin =
      pin.lat !== '' &&
      pin.lng !== '' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      (lat !== picked.lat || lng !== picked.lng)
    updateMut.mutate({
      formatted: picked.formatted_address,
      name: picked.name || undefined,
      place_id: picked.place_id,
      lat: Number.isFinite(lat) ? lat : undefined,
      lng: Number.isFinite(lng) ? lng : undefined,
      country: picked.components.country_code ?? undefined,
      street: picked.components.street ?? undefined,
      postal_code: picked.components.postal_code ?? undefined,
      city: picked.components.city ?? undefined,
      source: movedPin ? 'owner_pin' : 'places',
    })
  }

  function saveManual() {
    const street = manual.street.trim()
    const city = manual.city.trim()
    if (street.length < 3 || !city) {
      toast.error(t('admin.location.manual_invalid'))
      return
    }
    const line2 = [manual.postal.trim(), city].filter(Boolean).join(' ')
    updateMut.mutate({
      formatted: [street, line2, manual.country].filter(Boolean).join(', '),
      country: manual.country,
      street,
      postal_code: manual.postal.trim() || undefined,
      city,
      source: 'manual',
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="w-6 h-6 text-indigo-500" />
      </div>
    )
  }
  if (!data) return null

  const suggestion = data.pending ? data.place : null
  const current = data.place?.status === 'confirmed' ? data.place : null

  return (
    <div className="space-y-6 max-w-2xl" data-testid="location-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="location-title">
          {t('admin.location.title')}
        </h1>
        <p className="text-sm text-gray-500">{t('admin.location.subtitle')}</p>
      </div>

      {/* Sugestão do backfill (#230): confirmar em 1 toque */}
      {suggestion && mode === 'view' && (
        <div
          data-testid="location-suggestion"
          className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3"
        >
          <p className="text-sm font-bold text-amber-900">{t('admin.location.suggestion_title')}</p>
          <p className="text-sm text-amber-900">{suggestion.formatted_address}</p>
          <p className="text-xs text-amber-700">
            {t('admin.location.suggestion_confidence', {
              pct: String(Math.round((suggestion.location_confidence ?? 0.5) * 100)),
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => confirmMut.mutate()}
              disabled={confirmMut.isPending}
              data-testid="location-confirm-suggestion"
            >
              {confirmMut.isPending ? t('admin.common.saving') : t('admin.location.confirm')}
            </Button>
            <a
              href={suggestion.maps_url}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center text-sm text-indigo-600 hover:underline"
            >
              {t('admin.location.open_maps')}
            </a>
            <Button size="sm" variant="secondary" onClick={() => setMode('search')}>
              {t('admin.location.change')}
            </Button>
          </div>
        </div>
      )}

      {/* Estado atual */}
      {mode === 'view' && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-4 space-y-2"
          data-testid="location-current"
        >
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-gray-900">{t('admin.location.current')}</p>
            {data.confirmed ? (
              <Badge color="green">{t('admin.location.state_confirmed')}</Badge>
            ) : (
              <Badge color="yellow">{t('admin.location.state_pending')}</Badge>
            )}
          </div>
          {current ? (
            <>
              <p className="text-sm text-gray-700">{current.formatted_address}</p>
              {current.location_verified_at && (
                <p className="text-xs text-gray-500">
                  {t('admin.location.confirmed_at', {
                    date: new Date(current.location_verified_at * 1000).toLocaleString(),
                  })}
                </p>
              )}
              {current.location_source === 'manual' && (
                <p className="text-xs text-amber-700">{t('admin.location.manual_hint')}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">{t('admin.location.none')}</p>
          )}
          {data.maps_url && (
            <a
              href={data.maps_url}
              target="_blank"
              rel="noopener"
              className="inline-block text-sm text-indigo-600 hover:underline"
              data-testid="location-open-maps"
            >
              {t('admin.location.open_maps')}
            </a>
          )}
          <div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setMode('search')}
              data-testid="location-change"
            >
              {t('admin.location.change')}
            </Button>
          </div>
        </div>
      )}

      {/* Busca (Places server-side, #227) */}
      {mode === 'search' && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
          data-testid="location-search"
        >
          <Input
            label={t('admin.location.search_label')}
            value={query}
            placeholder={t('admin.location.search_placeholder')}
            onChange={(e) => {
              setQuery(e.target.value)
              setPicked(null)
              void search(e.target.value)
            }}
            data-testid="location-search-input"
          />
          {suggestBusy && <Spinner className="w-4 h-4 text-indigo-500" />}
          {suggestions.length > 0 && !picked && (
            <ul
              className="border border-gray-200 rounded-lg divide-y divide-gray-100"
              data-testid="location-suggestions"
            >
              {suggestions.map((s) => (
                <li key={s.place_id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50"
                    onClick={() => void choose(s.place_id)}
                  >
                    <span className="text-sm text-gray-900">{s.label}</span>
                    {s.secondary && (
                      <span className="block text-xs text-gray-500">{s.secondary}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {picked && (
            <div className="space-y-2 rounded-lg bg-gray-50 p-3" data-testid="location-picked">
              <p className="text-sm text-gray-900">{picked.formatted_address}</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label={t('admin.location.pin_lat')}
                  value={pin.lat}
                  inputMode="decimal"
                  onChange={(e) => setPin({ ...pin, lat: e.target.value })}
                />
                <Input
                  label={t('admin.location.pin_lng')}
                  value={pin.lng}
                  inputMode="decimal"
                  onChange={(e) => setPin({ ...pin, lng: e.target.value })}
                />
              </div>
              <p className="text-xs text-gray-500">{t('admin.location.pin_hint')}</p>
              <a
                href={picked.maps_url}
                target="_blank"
                rel="noopener"
                className="text-sm text-indigo-600 hover:underline"
              >
                {t('admin.location.open_maps')}
              </a>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setMode('manual')}>
              {t('admin.location.manual_toggle')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
              {t('admin.common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Manual (dark kitchen / sem provedor) */}
      {mode === 'manual' && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
          data-testid="location-manual"
        >
          <Input
            label={t('admin.location.street')}
            value={manual.street}
            onChange={(e) => setManual({ ...manual, street: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              label={t('admin.location.postal')}
              value={manual.postal}
              onChange={(e) => setManual({ ...manual, postal: e.target.value })}
            />
            <Input
              label={t('admin.location.city')}
              value={manual.city}
              onChange={(e) => setManual({ ...manual, city: e.target.value })}
            />
          </div>
          <Select
            label={t('admin.location.country')}
            value={manual.country}
            onChange={(e) => setManual({ ...manual, country: e.target.value })}
            options={[
              { value: 'PT', label: 'Portugal' },
              { value: 'BR', label: 'Brasil' },
              { value: 'ES', label: 'España' },
              { value: 'FR', label: 'France' },
              { value: 'DE', label: 'Deutschland' },
              { value: 'GB', label: 'United Kingdom' },
              { value: 'US', label: 'United States' },
            ]}
          />
          <p className="text-xs text-amber-700">{t('admin.location.manual_hint')}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setMode('search')}>
              {t('admin.location.search_toggle')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
              {t('admin.common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Reconfirmação obrigatória (#229): nada é salvo sem o check */}
      {(mode === 'search' || mode === 'manual') && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
          data-testid="location-confirm-box"
        >
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              data-testid="location-confirm-check"
            />
            {t('admin.location.confirm_check')}
          </label>
          {mode === 'search' ? (
            <Button
              onClick={() => savePicked()}
              disabled={!confirmChecked || !picked || updateMut.isPending}
              data-testid="location-save"
            >
              {updateMut.isPending ? t('admin.common.saving') : t('admin.location.save')}
            </Button>
          ) : (
            <Button
              onClick={() => saveManual()}
              disabled={!confirmChecked || updateMut.isPending}
              data-testid="location-save"
            >
              {updateMut.isPending ? t('admin.common.saving') : t('admin.location.save')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
